import type { ChatType } from '@kanbu/schema';
import { Role } from '@kanbu/schema/enums';
import { DateFormat, formatUtils } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@utima/ui';
import { FileScan, BugPlayIcon, Copy } from 'lucide-react';
import { useMemo } from 'react';

import { SimpleTooltip } from '@/components';
import { ActionsCell } from '@/components/dataTable/cells/ActionsCell';
import { Protected } from '@/components/protected/Protected';
import { AppSettings } from '@/constants/AppSettings';
import { useAccess } from '@/hooks/useAccess';

import { useChatMutation } from './useChatMutation';

const columnHelper = createColumnHelper<ChatType>();

export function useChatsCols() {
  const { t } = useLingui();
  const { hasAccess } = useAccess();
  const {
    remove: { mutateAsync: remove },
    duplicate: { mutateAsync: duplicate },
  } = useChatMutation();

  return useMemo(
    () => [
      columnHelper.accessor(row => row.id, {
        id: 'id',
        header: t`Id`,
        meta: {
          sortable: 'id',
        },
      }),
      columnHelper.accessor(row => row.name, {
        id: 'name',
        header: t`Name`,
        meta: {
          sortable: 'name',
        },
      }),
      columnHelper.accessor(row => row.organization?.name, {
        id: 'organization',
        header: t`Organization`,
        meta: {
          sortable: 'organization.name',
        },
      }),
      ...(hasAccess([Role.SuperAdmin])
        ? [
            columnHelper.accessor(row => row.agent, {
              id: 'agent',
              header: t`Agent`,
              cell: row => (
                <span className='whitespace-nowrap font-mono'>
                  {row.getValue()}
                </span>
              ),
              meta: {
                sortable: 'agent',
              },
            }),
            columnHelper.accessor(row => row.model, {
              id: 'model',
              header: t`Model`,
              cell: row => (
                <span className='whitespace-nowrap font-mono'>
                  {row.getValue()}
                </span>
              ),
              meta: {
                sortable: 'model',
              },
            }),
          ]
        : []),
      columnHelper.accessor(row => row.documentsCount, {
        id: 'documentsCount',
        header: t`Documents count`,
        meta: {
          align: 'right',
          sortable: 'documentsCount',
        },
      }),
      columnHelper.accessor(row => row.embeddingsCount, {
        id: 'embeddingsCount',
        header: t`Embeddings count`,
        cell: row => row.getValue() ?? '0',
        meta: {
          align: 'right',
          sortable: 'embeddingsCount',
        },
      }),
      columnHelper.accessor(row => row.updatedAt, {
        id: 'updatedAt',
        header: t`Updated`,
        cell: row => formatUtils.date(row.getValue(), DateFormat.DateTime),
        meta: {
          sortable: 'updatedAt',
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: ({ row }) => (
          <ActionsCell onDelete={() => remove({ id: row.original.id })}>
            <Protected roles={[Role.SuperAdmin]}>
              <SimpleTooltip title={t`Duplicate`}>
                <Button
                  data-cta='admin-chats-duplicate-button'
                  variant='ghost'
                  size='icon-xs'
                  className='text-info'
                  onClick={() => duplicate({ id: row.original.id })}
                  outline
                >
                  <Copy className='size-4' />
                </Button>
              </SimpleTooltip>
            </Protected>
            <SimpleTooltip title={t`Playground link`}>
              <Button
                data-cta='admin-chats-duplicate-button'
                variant='ghost'
                size='icon-xs'
                className='text-emerald-500'
                outline
                asChild
              >
                <a
                  target='_blank'
                  href={`${AppSettings.playgroundUrl}/${row.original.id}`}
                  rel='noreferrer'
                >
                  <BugPlayIcon className='size-4' />
                </a>
              </Button>
            </SimpleTooltip>
            <SimpleTooltip title={t`Knowledge Base`}>
              <Button
                data-cta='admin-chats-documents-button'
                variant='ghost'
                size='icon-xs'
                outline
                asChild
              >
                <Link
                  to='/chats/$chatId/documents'
                  params={{ chatId: row.original.id }}
                >
                  <FileScan className='size-4' />
                </Link>
              </Button>
            </SimpleTooltip>
          </ActionsCell>
        ),
        meta: {
          align: 'center',
        },
      }),
    ],
    [hasAccess, remove, duplicate, t],
  );
}
